import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, MapPin, Heart, Sparkles, GraduationCap, Calendar } from "lucide-react";
import { API, setMeta, fmtErr } from "./api";
import { T, TTitle, TField, TConsent, TBadge, THeader, TFooter, YtEmbed } from "./ui";

const INTERETS = [
  { value: "soiree", label: "Soirée de Rencontre & Ressourcement" },
  { value: "soin", label: "Soin LE GUÉRISSEUR" },
  { value: "journee", label: "Retrouver son énergie collectivement" },
  { value: "formation", label: "Formation : Devenir Énergéticien de la Conscience" },
];

const TYPES_LIEU = ["Salle de réunion", "Salle yoga / Qi Gong", "Espace bien-être", "Cabinet", "Centre associatif", "Café alternatif", "Autre"];

const ETAPES = [
  { icon: MapPin, t: "Propose ta ville", d: "Indique où tu aimerais que la tournée s'arrête." },
  { icon: Heart, t: "Propose un lieu", d: "Une salle, un espace bien-être, un cabinet… aide-moi à trouver le cadre adapté." },
  { icon: Sparkles, t: "Villes prioritaires", d: "Les villes les plus demandées deviennent les prochaines étapes." },
  { icon: Calendar, t: "Réservations via Tidycal", d: "Quand une étape s'ouvre, tu réserves ta place en quelques clics." },
  { icon: GraduationCap, t: "Email de confirmation", d: "Tu reçois une confirmation par email avec toutes les informations pratiques." },
];

const emptyVille = { prenom: "", nom: "", email: "", telephone: "", ville: "", pays: "", interet: "soiree", consentement: false };
const emptyLieu = { prenom: "", nom: "", email: "", telephone: "", ville: "", pays: "", type_lieu: TYPES_LIEU[0], nom_lieu: "", adresse: "", maps_url: "", capacite: "", contact: "", disponibilites: "", commentaires: "", consentement: false };
const emptyPart = { prenom: "", email: "", ville: "", creneau: "les_deux", alerte: true, consentement: false };

// Calendrier PROVISOIRE par défaut — valeur de repli si l'API n'en renvoie pas
// (la source de vérité est la base, éditable dans Admin Tournée → onglet Calendrier)
const CALENDRIER_DEFAUT = [
  { mois: "Janvier 2027", villes: ["Perpignan", "Narbonne", "Montpellier"] },
  { mois: "Février 2027", villes: ["Marseille", "Toulon", "Nice"] },
  { mois: "Mars 2027", villes: ["Toulouse", "Bordeaux"] },
  { mois: "Avril 2027", villes: ["Lyon", "Genève"] },
  { mois: "Mai 2027", villes: ["Strasbourg", "Metz"] },
  { mois: "Juin 2027", villes: ["Luxembourg"] },
];

const FAQ_TOURNEE = [
  {
    q: "Les dates sont-elles définitives ?",
    a: "Non : ce calendrier est provisoire. Chaque étape est confirmée selon le nombre d'inscrits dans la ville — d'où l'importance de signaler ton intérêt dès maintenant.",
  },
  {
    q: "Comment être prévenu·e quand ma ville est confirmée ?",
    a: "Remplis le formulaire « Je veux participer » et laisse cochée la case de notification : tu recevras un email dès que ta ville est confirmée, avec les dates et le lieu définitifs.",
  },
  {
    q: "Que se passe-t-il lors d'une étape ?",
    a: "Une Soirée de Rencontre & Ressourcement le vendredi (gratuite ou participation libre), des créneaux de Soin LE GUÉRISSEUR le samedi, et la journée « Retrouver son énergie collectivement » le dimanche, avec présentation de la formation.",
  },
  {
    q: "Combien coûte une étape ?",
    a: "La soirée est gratuite ou à participation libre. Les soins et la journée collective sont proposés à tarif doux, communiqué lors de la confirmation de l'étape. Les réservations passent par Tidycal.",
  },
];

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function TourneeLanding() {
  const [data, setData] = useState(null);
  const [ville, setVille] = useState(emptyVille);
  const [lieu, setLieu] = useState(emptyLieu);
  const [part, setPart] = useState(emptyPart);
  const [stVille, setStVille] = useState({ state: "idle", msg: "" });
  const [stLieu, setStLieu] = useState({ state: "idle", msg: "" });
  const [stPart, setStPart] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    setMeta(
      "La Tournée Le Guérisseur — Soins, Rencontres & Formation",
      "La Tournée Le Guérisseur arrive bientôt dans votre ville : soins, rencontres et formation. Proposez votre ville ou un lieu."
    );
    axios.get(`${API}/bootstrap`).then(({ data }) => setData(data)).catch(() => setData({ settings: {}, demandes: [], videos: [] }));
  }, []);

  const settings = data?.settings || {};
  const seuil = Number(settings.seuil_confirmation) || 15;
  // Calendrier : données de la base (éditables dans l'admin), repli sur le défaut
  const calendrier = data?.calendrier?.length ? data.calendrier : CALENDRIER_DEFAUT;

  // Compteurs d'intérêt par ville (données réelles des formulaires)
  const compteurs = {};
  (data?.demandes || []).forEach((d) => {
    compteurs[String(d.ville || "").trim().toLowerCase()] = d.count;
  });
  const compteDe = (v) => compteurs[v.trim().toLowerCase()] || 0;

  const choisirVille = (v) => {
    setPart((p) => ({ ...p, ville: v }));
    setStPart({ state: "idle", msg: "" });
    scrollTo("#participer");
  };

  const submitPart = async (e) => {
    e.preventDefault();
    setStPart({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/demandes`, {
        prenom: part.prenom, nom: "", email: part.email, telephone: "",
        ville: part.ville, pays: "", interet: part.creneau,
        alerte: part.alerte, source: "calendrier", consentement: part.consentement,
      });
      setStPart({ state: "ok", msg: part.alerte ? "C'est noté ! Tu seras prévenu·e par email dès que ta ville est confirmée." : "C'est noté ! Merci pour ton intérêt." });
      setPart(emptyPart);
      const { data: fresh } = await axios.get(`${API}/bootstrap`);
      setData(fresh);
    } catch (err) {
      setStPart({ state: "err", msg: fmtErr(err) });
    }
  };

  const submitVille = async (e) => {
    e.preventDefault();
    setStVille({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/demandes`, ville);
      setStVille({ state: "ok", msg: "Merci ! Ta ville est bien enregistrée. Tu recevras un email de confirmation." });
      setVille(emptyVille);
      const { data } = await axios.get(`${API}/bootstrap`);
      setData(data);
    } catch (err) {
      setStVille({ state: "err", msg: fmtErr(err) });
    }
  };

  const submitLieu = async (e) => {
    e.preventDefault();
    setStLieu({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/lieux`, lieu);
      setStLieu({ state: "ok", msg: "Merci ! Ta proposition de lieu est bien enregistrée. Tu recevras un email de confirmation." });
      setLieu(emptyLieu);
    } catch (err) {
      setStLieu({ state: "err", msg: fmtErr(err) });
    }
  };

  return (
    <div data-testid="tournee-page" className="bg-ivory/60 dark:bg-night/60 min-h-screen">
      <THeader right={
        <button data-testid="tournee-cta-ville-top" onClick={() => scrollTo("#participer")} className={T.gold}>
          Je veux participer
        </button>
      } />

      {/* 1 — HERO */}
      <section data-testid="tournee-hero" className="relative py-24 sm:py-32 text-center px-6 overflow-hidden">
        {/* Le van de la tournée en arrière-plan — animation Ken Burns comme la page d'accueil */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="/images/tournee-van.webp"
            alt="Le van de la Tournée Le Guérisseur sur les routes"
            data-testid="tournee-hero-van"
            className="ken-burns w-full h-full object-cover object-center opacity-50 dark:opacity-40"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ivory/85 via-ivory/65 to-ivory dark:from-night/90 dark:via-night/78 dark:to-night" />
          <div className="absolute inset-x-0 bottom-0 h-px gold-divider opacity-60" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <TBadge testid="tournee-badge">À venir</TBadge>
          <h1 data-testid="tournee-h1" className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ink dark:text-whisper">
            La Tournée <span className="text-forest dark:text-gold">LE GUÉRISSEUR</span> arrive bientôt dans votre ville
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone dark:text-mist leading-relaxed max-w-2xl mx-auto">
            Indique la ville où tu souhaites que je passe et aide-moi à trouver un lieu adapté.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button data-testid="tournee-cta-ville" onClick={() => scrollTo("#calendrier")} className={T.forest}>
              Choisir ma ville <ArrowRight size={14} />
            </button>
            <button data-testid="tournee-cta-lieu" onClick={() => scrollTo("#participer")} className={T.outline}>
              Je veux participer
            </button>
          </div>
        </div>
      </section>

      {/* CALENDRIER PROVISOIRE — villes cliquables + compteurs d'intérêt */}
      <section id="calendrier" data-testid="tournee-calendrier" className="py-20 sm:py-28 bg-sand/70 dark:bg-onyx/70">
        <div className="max-w-5xl mx-auto px-6">
          <TTitle
            eyebrow={`${calendrier[0]?.mois || ""} → ${calendrier[calendrier.length - 1]?.mois || ""}`}
            title="Le calendrier de la tournée"
            lead={`Calendrier provisoire : une étape est confirmée à partir de ${seuil} intéressé·es dans la ville. Clique sur ta ville pour signaler ton intérêt.`}
          />
          <div className="space-y-8">
            {calendrier.map((m) => (
              <div key={m.mois} data-testid={`calendrier-${m.mois.toLowerCase().replace(/[^a-z0-9]/g, "")}`} className="grid sm:grid-cols-[140px_1fr] gap-3 sm:gap-6 items-start">
                <p className="font-serif text-2xl text-forest dark:text-gold sm:text-right sm:pt-2">{m.mois}</p>
                <div className="flex flex-wrap gap-2.5">
                  {m.villes.map((v) => {
                    const n = compteDe(v);
                    const atteint = n >= seuil;
                    const pct = Math.min(100, Math.round((n / seuil) * 100));
                    return (
                      <button
                        key={v}
                        data-testid={`cal-ville-${v.toLowerCase().replace(/[^a-z]/g, "")}`}
                        onClick={() => choisirVille(v)}
                        className={`group inline-flex flex-col items-stretch gap-1.5 rounded-2xl border px-5 py-3 text-sm text-left transition-[border-color,transform] duration-300 hover:-translate-y-0.5 cursor-pointer ${atteint ? "border-gold bg-gold/10" : "border-beige dark:border-charcoal bg-ivory/80 dark:bg-night/80 hover:border-gold"}`}
                      >
                        <span className="inline-flex items-center gap-2 text-ink dark:text-whisper">
                          <MapPin size={13} className="text-forest dark:text-gold shrink-0" />
                          {v}
                          <span className="text-[10px] tracking-[0.08em] uppercase text-stone dark:text-mist group-hover:text-gold transition-colors">
                            {n > 0 ? `${n}/${seuil} intéressé·e${n > 1 ? "s" : ""}` : `0/${seuil} · à lancer`}
                          </span>
                        </span>
                        <span className="block h-1 w-full rounded-full bg-beige/70 dark:bg-charcoal overflow-hidden">
                          <span data-testid={`cal-progress-${v.toLowerCase().replace(/[^a-z]/g, "")}`} className="block h-full bg-gold transition-all duration-700" style={{ width: `${pct}%` }} />
                        </span>
                        {atteint && <span className="text-[9px] tracking-[0.14em] uppercase text-gold">Objectif atteint — confirmation proche</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <p data-testid="calendrier-note" className="mt-10 text-center text-xs sm:text-sm text-stone dark:text-mist italic max-w-xl mx-auto">
            Itinéraire provisoire : le Sud en janvier, puis la remontée vers Toulouse, Bordeaux, Lyon, Genève, Strasbourg, Metz et Luxembourg jusqu'en juin. Ta ville n'y est pas ? Propose-la plus bas.
          </p>
        </div>
      </section>

      {/* FORMULAIRE COURT — JE VEUX PARTICIPER */}
      <section id="participer" data-testid="tournee-participer" className="py-20 sm:py-28">
        <div className="max-w-xl mx-auto px-6">
          <TTitle eyebrow="Ça m'intéresse" title="Je veux participer" lead="Trente secondes : dis-moi qui tu es, ta ville et ton créneau — je te préviens dès qu'elle est confirmée." />
          {stPart.state === "ok" ? (
            <div data-testid="participer-success" className={`${T.card} text-center`}>
              <Sparkles size={28} className="text-gold mx-auto mb-4" strokeWidth={1.25} />
              <p className="font-serif text-2xl text-ink dark:text-whisper mb-2">Merci !</p>
              <p className="text-sm text-stone dark:text-mist">{stPart.msg}</p>
              <button data-testid="participer-again" onClick={() => setStPart({ state: "idle", msg: "" })} className={`${T.outline} mt-6`}>Ajouter une autre ville</button>
            </div>
          ) : (
            <form onSubmit={submitPart} className={`${T.card} space-y-4`}>
              <TField label="Prénom" required><input data-testid="part-prenom" className={T.input} required value={part.prenom} onChange={(e) => setPart({ ...part, prenom: e.target.value })} /></TField>
              <TField label="Email" required><input data-testid="part-email" type="email" className={T.input} required value={part.email} onChange={(e) => setPart({ ...part, email: e.target.value })} /></TField>
              <TField label="Ville souhaitée" required><input data-testid="part-ville" className={T.input} required placeholder="Ex : Montpellier" value={part.ville} onChange={(e) => setPart({ ...part, ville: e.target.value })} /></TField>
              <TField label="Créneau souhaité" required>
                <select data-testid="part-creneau" className={T.input} value={part.creneau} onChange={(e) => setPart({ ...part, creneau: e.target.value })}>
                  <option value="soiree">Soirée (vendredi)</option>
                  <option value="soin">Soin individuel (samedi)</option>
                  <option value="journee">Journée (dimanche)</option>
                  <option value="les_deux">Tout le week-end</option>
                </select>
              </TField>
              <TConsent testid="part-alerte" checked={part.alerte} onChange={(v) => setPart({ ...part, alerte: v })} text="Prévenez-moi quand ma ville est confirmée." />
              <TConsent testid="part-consentement" checked={part.consentement} onChange={(v) => setPart({ ...part, consentement: v })} text={settings.rgpd_text || "J'accepte le traitement de mes données."} />
              {stPart.state === "err" && <p data-testid="part-error" className="text-sm text-red-700 dark:text-red-400">{stPart.msg}</p>}
              <button data-testid="part-submit" type="submit" disabled={stPart.state === "sending" || !part.consentement} className={`${T.gold} w-full disabled:opacity-50`}>
                {stPart.state === "sending" ? "Envoi…" : "Je veux participer"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* 2 — FORMULAIRE PROPOSER MA VILLE */}
      <section id="proposer-ville" data-testid="tournee-form-ville" className="py-20 sm:py-28 bg-sand/70 dark:bg-onyx/70">
        <div className="max-w-2xl mx-auto px-6">
          <TTitle eyebrow="Étape 1" title="Proposer ma ville" lead="Dis-moi où tu es et ce qui t'intéresse : les villes les plus demandées deviennent des étapes." />
          {stVille.state === "ok" ? (
            <div data-testid="tournee-ville-success" className={`${T.card} text-center`}>
              <Sparkles size={28} className="text-gold mx-auto mb-4" strokeWidth={1.25} />
              <p className="font-serif text-2xl text-ink dark:text-whisper mb-2">Merci {ville.prenom || "à toi"} !</p>
              <p className="text-sm text-stone dark:text-mist">{stVille.msg}</p>
              <button data-testid="tournee-ville-again" onClick={() => setStVille({ state: "idle", msg: "" })} className={`${T.outline} mt-6`}>Proposer une autre ville</button>
            </div>
          ) : (
            <form onSubmit={submitVille} className={`${T.card} space-y-4`}>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Prénom" required><input data-testid="demande-prenom" className={T.input} required value={ville.prenom} onChange={(e) => setVille({ ...ville, prenom: e.target.value })} /></TField>
                <TField label="Nom" required><input data-testid="demande-nom" className={T.input} required value={ville.nom} onChange={(e) => setVille({ ...ville, nom: e.target.value })} /></TField>
              </div>
              <TField label="Email" required><input data-testid="demande-email" type="email" className={T.input} required value={ville.email} onChange={(e) => setVille({ ...ville, email: e.target.value })} /></TField>
              <TField label="Téléphone"><input data-testid="demande-telephone" type="tel" className={T.input} value={ville.telephone} onChange={(e) => setVille({ ...ville, telephone: e.target.value })} /></TField>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Ville" required><input data-testid="demande-ville" className={T.input} required value={ville.ville} onChange={(e) => setVille({ ...ville, ville: e.target.value })} /></TField>
                <TField label="Pays" required><input data-testid="demande-pays" className={T.input} required value={ville.pays} onChange={(e) => setVille({ ...ville, pays: e.target.value })} /></TField>
              </div>
              <TField label="Ce qui t'intéresse" required>
                <select data-testid="demande-interet" className={T.input} value={ville.interet} onChange={(e) => setVille({ ...ville, interet: e.target.value })}>
                  {INTERETS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </TField>
              <TConsent testid="demande-consentement" checked={ville.consentement} onChange={(v) => setVille({ ...ville, consentement: v })} text={settings.rgpd_text || "J'accepte le traitement de mes données."} />
              {stVille.state === "err" && <p data-testid="demande-error" className="text-sm text-red-700 dark:text-red-400">{stVille.msg}</p>}
              <button data-testid="demande-submit" type="submit" disabled={stVille.state === "sending" || !ville.consentement} className={`${T.forest} w-full disabled:opacity-50`}>
                {stVille.state === "sending" ? "Envoi…" : "Proposer ma ville"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* 3 — FORMULAIRE PROPOSER UN LIEU */}
      <section id="proposer-lieu" data-testid="tournee-form-lieu" className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-6">
          <TTitle eyebrow="Étape 2" title="Proposer un lieu" lead="Une salle, un espace bien-être, un cabinet… chaque proposition compte pour construire l'étape." />
          {stLieu.state === "ok" ? (
            <div data-testid="tournee-lieu-success" className={`${T.card} text-center`}>
              <Heart size={28} className="text-gold mx-auto mb-4" strokeWidth={1.25} />
              <p className="font-serif text-2xl text-ink dark:text-whisper mb-2">Merci !</p>
              <p className="text-sm text-stone dark:text-mist">{stLieu.msg}</p>
              <button data-testid="tournee-lieu-again" onClick={() => setStLieu({ state: "idle", msg: "" })} className={`${T.outline} mt-6`}>Proposer un autre lieu</button>
            </div>
          ) : (
            <form onSubmit={submitLieu} className={`${T.card} space-y-4`}>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Prénom" required><input data-testid="lieu-prenom" className={T.input} required value={lieu.prenom} onChange={(e) => setLieu({ ...lieu, prenom: e.target.value })} /></TField>
                <TField label="Nom" required><input data-testid="lieu-nom" className={T.input} required value={lieu.nom} onChange={(e) => setLieu({ ...lieu, nom: e.target.value })} /></TField>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Email" required><input data-testid="lieu-email" type="email" className={T.input} required value={lieu.email} onChange={(e) => setLieu({ ...lieu, email: e.target.value })} /></TField>
                <TField label="Téléphone"><input data-testid="lieu-telephone" type="tel" className={T.input} value={lieu.telephone} onChange={(e) => setLieu({ ...lieu, telephone: e.target.value })} /></TField>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Ville" required><input data-testid="lieu-ville" className={T.input} required value={lieu.ville} onChange={(e) => setLieu({ ...lieu, ville: e.target.value })} /></TField>
                <TField label="Pays" required><input data-testid="lieu-pays" className={T.input} required value={lieu.pays} onChange={(e) => setLieu({ ...lieu, pays: e.target.value })} /></TField>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Type de lieu" required>
                  <select data-testid="lieu-type" className={T.input} value={lieu.type_lieu} onChange={(e) => setLieu({ ...lieu, type_lieu: e.target.value })}>
                    {TYPES_LIEU.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </TField>
                <TField label="Nom du lieu" required><input data-testid="lieu-nom-lieu" className={T.input} required value={lieu.nom_lieu} onChange={(e) => setLieu({ ...lieu, nom_lieu: e.target.value })} /></TField>
              </div>
              <TField label="Adresse"><input data-testid="lieu-adresse" className={T.input} value={lieu.adresse} onChange={(e) => setLieu({ ...lieu, adresse: e.target.value })} /></TField>
              <div className="grid sm:grid-cols-2 gap-4">
                <TField label="Lien Google Maps"><input data-testid="lieu-maps" type="url" className={T.input} placeholder="https://maps.google.com/…" value={lieu.maps_url} onChange={(e) => setLieu({ ...lieu, maps_url: e.target.value })} /></TField>
                <TField label="Capacité"><input data-testid="lieu-capacite" className={T.input} placeholder="Ex : 20 personnes" value={lieu.capacite} onChange={(e) => setLieu({ ...lieu, capacite: e.target.value })} /></TField>
              </div>
              <TField label="Contact sur place"><input data-testid="lieu-contact" className={T.input} value={lieu.contact} onChange={(e) => setLieu({ ...lieu, contact: e.target.value })} /></TField>
              <TField label="Disponibilités"><input data-testid="lieu-dispo" className={T.input} placeholder="Ex : week-ends, soirs…" value={lieu.disponibilites} onChange={(e) => setLieu({ ...lieu, disponibilites: e.target.value })} /></TField>
              <TField label="Commentaires"><textarea data-testid="lieu-commentaires" rows={3} className={T.input} value={lieu.commentaires} onChange={(e) => setLieu({ ...lieu, commentaires: e.target.value })} /></TField>
              <TConsent testid="lieu-consentement" checked={lieu.consentement} onChange={(v) => setLieu({ ...lieu, consentement: v })} text={settings.rgpd_text || "J'accepte le traitement de mes données."} />
              {stLieu.state === "err" && <p data-testid="lieu-error" className="text-sm text-red-700 dark:text-red-400">{stLieu.msg}</p>}
              <button data-testid="lieu-submit" type="submit" disabled={stLieu.state === "sending" || !lieu.consentement} className={`${T.forest} w-full disabled:opacity-50`}>
                {stLieu.state === "sending" ? "Envoi…" : "Proposer un lieu"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* 4 — VILLES DEMANDÉES */}
      {settings.show_demandes && (data?.demandes || []).length > 0 && (
        <section data-testid="tournee-villes-demande" className="py-20 sm:py-28 bg-sand/70 dark:bg-onyx/70">
          <div className="max-w-4xl mx-auto px-6">
            <TTitle eyebrow="La carte de la tournée" title="Villes demandées" lead="Les villes les plus demandées deviennent prioritaires." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.demandes.map((d, i) => (
                <div key={`${d.ville}-${d.pays}`} data-testid={`demande-ville-card-${i}`} className={`${T.card} !p-5 flex items-center gap-4`}>
                  <MapPin size={20} className="text-forest dark:text-gold shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-ink dark:text-whisper truncate">{d.ville} <span className="text-sm text-stone dark:text-mist">· {d.pays}</span></p>
                    <p className="text-xs text-stone dark:text-mist">{d.count} demande{d.count > 1 ? "s" : ""} · {d.statut === "retenue" ? "Ville retenue" : d.statut === "en_cours" ? "À l'étude" : "En attente"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5 — VIDÉOS */}
      {settings.show_videos && (data?.videos || []).length > 0 && (
        <section data-testid="tournee-videos" className="py-20 sm:py-28">
          <div className="max-w-5xl mx-auto px-6">
            <TTitle eyebrow="En images" title="Vidéos" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.videos.slice(0, 3).map((v, i) => (
                <div key={v.id}>
                  <YtEmbed url={v.youtube_url} title={v.titre} testid={`tournee-video-${i}`} />
                  {v.titre && <p className="mt-3 font-serif text-lg text-ink dark:text-whisper text-center">{v.titre}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6 — LES PROCHAINES ÉTAPES */}
      <section data-testid="tournee-etapes" className="py-20 sm:py-28 bg-sand/70 dark:bg-onyx/70">
        <div className="max-w-6xl mx-auto px-6">
          <TTitle eyebrow="Comment ça marche" title="Les prochaines étapes" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {ETAPES.map((e, i) => (
              <div key={e.t} data-testid={`tournee-etape-${i}`} className={`${T.card} text-center`}>
                <e.icon size={24} className="text-forest dark:text-gold mx-auto mb-4" strokeWidth={1.25} />
                <p className="font-serif text-lg text-ink dark:text-whisper leading-snug mb-2">{e.t}</p>
                <p className="text-xs text-stone dark:text-mist leading-relaxed">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ TOURNÉE */}
      <section data-testid="tournee-faq" className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <TTitle eyebrow="Questions fréquentes" title="FAQ de la tournée" />
          <div className="space-y-3">
            {FAQ_TOURNEE.map((f, i) => (
              <details key={f.q} data-testid={`tournee-faq-${i}`} className={`${T.card} !p-5 group`}>
                <summary className="cursor-pointer font-medium text-ink dark:text-whisper list-none flex items-center justify-between gap-4">
                  {f.q}
                  <ArrowRight size={14} className="text-gold shrink-0 transition-transform duration-300 group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-stone dark:text-mist leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — CTA FINAL */}
      <section data-testid="tournee-cta-final" className="py-20 sm:py-28 text-center px-6 bg-sand/70 dark:bg-onyx/70">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink dark:text-whisper">Et si la tournée s'arrêtait chez toi ?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button data-testid="tournee-final-ville" onClick={() => scrollTo("#participer")} className={T.gold}>Je veux participer</button>
            <button data-testid="tournee-final-lieu" onClick={() => scrollTo("#proposer-lieu")} className={T.outline}>Proposer un lieu</button>
          </div>
        </div>
      </section>

      <TFooter settings={settings} />
    </div>
  );
}
